// One flow against Klaviyo (7.2 M4): per day and letter step how our shadow
// decisions and Klaviyo's real letters compared, the days a new version was
// published marked (an editor change shows from that day: M7), the reasons
// behind the differences, the differences themselves a page at a time — and a
// click on one opens the profile's two timelines side by side, on one time
// axis, the difference highlighted.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  fetchFlowComparison,
  fetchFlowComparisonItems,
  fetchFlowComparisonTimeline,
} from "@/lib/api";
import type {
  FlowComparisonDayRow,
  FlowComparisonItem,
  FlowComparisonSplit,
  FlowComparisonStepDay,
} from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { TONE_FILL } from "@/components/emc/tone";
import {
  OUTCOMES,
  dayStatusLook,
  deltaText,
  outcomeLook,
  timelineRows,
  timelineText,
  versionMarks,
} from "@/pages/flows/compareModel";

const RANGES = [
  { value: "7", label: "Az utolsó 7 nap" },
  { value: "14", label: "Az utolsó 14 nap" },
  { value: "30", label: "Az utolsó 30 nap" },
];
const PAGE = 50;

function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/** One step's day as a bar: every outcome its share, in its tone. */
function OutcomeBar({ step }: { step: FlowComparisonStepDay | undefined }) {
  if (!step) return <span className="text-muted-foreground">—</span>;
  const total = OUTCOMES.reduce((sum, outcome) => sum + (step.outcomes[outcome] ?? 0), 0);
  if (total === 0) return <span className="text-muted-foreground">—</span>;
  const title = OUTCOMES.filter((outcome) => (step.outcomes[outcome] ?? 0) > 0)
    .map((outcome) => `${outcomeLook(outcome).label}: ${count(step.outcomes[outcome])}`)
    .join("\n");
  const differ = total - (step.outcomes.match ?? 0) - (step.outcomes.out_of_window ?? 0);
  return (
    <span className="flex min-w-28 flex-col gap-1" title={title}>
      <span className="flex h-1.5 w-full overflow-hidden rounded-pill bg-fill-secondary">
        {OUTCOMES.map((outcome) => {
          const value = step.outcomes[outcome] ?? 0;
          if (value === 0) return null;
          return (
            <span
              key={outcome}
              className={TONE_FILL[outcomeLook(outcome).tone]}
              style={{ width: `${(value / total) * 100}%` }}
            />
          );
        })}
      </span>
      <span className="data-mono text-label text-muted-foreground">
        {count(step.outcomes.match ?? 0)} egyezik · {count(differ)} eltér
      </span>
    </span>
  );
}

function Timeline({
  flowId,
  item,
  onClose,
}: {
  flowId: number;
  item: FlowComparisonItem | null;
  onClose: () => void;
}) {
  const [moves, setMoves] = useState(false);
  const who = item?.profile_source_id ?? (item?.contact_id ? String(item.contact_id) : null);
  const around = item?.ours_at ?? item?.klaviyo_at ?? null;
  const [state] = useLoad(
    () =>
      who === null
        ? Promise.resolve(null)
        : fetchFlowComparisonTimeline(flowId, who, { around, days: 7 }),
    [flowId, who, around],
  );
  const rows = state.data ? timelineRows(state.data.items, item, { moves }) : [];
  return (
    <Sheet open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="max-w-content">
        <SheetHeader>
          <SheetTitle>A profil két idővonala</SheetTitle>
          <SheetDescription>
            Bal oldalt a Klaviyo eseményei, jobb oldalt a mi futásaink és döntéseink, egy időtengelyen (±7 nap).
            {item && (
              <>
                {" "}
                Az eltérés: <strong>{outcomeLook(item.outcome).label}</strong>
                {item.reason_label ? ` — ${item.reason_label}` : ""}.
              </>
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-3 overflow-y-auto px-4 pb-6">
          <label className="flex cursor-pointer items-center gap-2 text-detail">
            <Checkbox checked={moves} onChange={(event) => setMoves(event.target.checked)} />
            a futások minden mozgása is
          </label>
          {state.error !== null && <ErrorState message={state.error} />}
          {state.data === null && state.error === null && <LoadingState lines={4} />}
          {state.data !== null && rows.length === 0 && (
            <EmptyState bare title="Nincs adat" description="Ebben az ablakban nincs esemény és futás." />
          )}
          {rows.length > 0 && (
            <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 text-detail">
              <span className="caps-label pb-1">idő</span>
              <span className="caps-label pb-1">Klaviyo</span>
              <span className="caps-label pb-1">mi (árnyék)</span>
              {rows.map((row, index) => (
                <div
                  key={`${row.at}-${index}`}
                  className={cn(
                    "col-span-3 grid grid-cols-subgrid border-t border-line py-1",
                    row.highlight && "bg-orange-soft",
                  )}
                >
                  <span className="data-mono text-muted-foreground">{minute(row.at)}</span>
                  <span className={cn(row.klaviyo?.this_flow && "font-medium")}>
                    {row.klaviyo ? timelineText(row.klaviyo) : ""}
                  </span>
                  <span className={cn(row.emc?.kind === "decision" && "font-medium")}>
                    {row.emc ? timelineText(row.emc) : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function FlowComparison() {
  const params = useParams();
  const flowId = Number(params.id);
  const [range, setRange] = useState("14");
  const [detail, reloadDetail] = useLoad(
    () => fetchFlowComparison(flowId, { from: daysAgo(Number(range)) }),
    [flowId, range],
  );
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [day, setDay] = useState("");
  const [step, setStep] = useState("");
  const [items, setItems] = useState<FlowComparisonItem[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [open, setOpen] = useState<FlowComparisonItem | null>(null);
  const [list, reloadList] = useLoad(async () => {
    const page = await fetchFlowComparisonItems(flowId, {
      outcome,
      reason,
      day,
      step,
      differences: outcome === "",
      limit: PAGE,
    });
    setItems(page.items);
    setCursor(page.next);
    return page;
  }, [flowId, outcome, reason, day, step]);

  async function more() {
    if (cursor === null) return;
    const page = await fetchFlowComparisonItems(flowId, {
      outcome,
      reason,
      day,
      step,
      differences: outcome === "",
      cursor,
      limit: PAGE,
    });
    setItems((previous) => [...previous, ...page.items]);
    setCursor(page.next);
  }

  const data = detail.data;
  const marks = data ? versionMarks(data.versions) : {};
  const steps = data?.steps ?? [];
  const days = [...(data?.days ?? [])].reverse();

  const dayColumns: ColumnDef<FlowComparisonDayRow>[] = [
    {
      id: "day",
      header: "Nap",
      cell: ({ row }) => {
        const look = dayStatusLook(row.original.status);
        const published = marks[row.original.day];
        return (
          <span className="flex flex-col items-start gap-1">
            <span className="data-mono">{row.original.day}</span>
            <StatusPill tone={look.tone} title={[look.title, ...row.original.pending.map((each) => each.text)].join("\n")}>
              {look.label}
            </StatusPill>
            {published && (
              <StatusPill tone="scheduled" title="Ezen a napon új verzió lett érvényes">
                új verzió: {published.map((number) => `${number}.`).join(", ")}
              </StatusPill>
            )}
          </span>
        );
      },
    },
    ...steps.map(
      (each): ColumnDef<FlowComparisonDayRow> => ({
        id: `step-${each.step_id}`,
        header: () => (
          <span title={each.name ?? undefined}>
            #{each.step_id}
            {each.name ? <span className="block text-label text-muted-foreground">{each.name}</span> : null}
          </span>
        ),
        cell: ({ row }) => (
          <button
            type="button"
            className="w-full text-left"
            onClick={(event) => {
              event.stopPropagation();
              setDay(row.original.day);
              setStep(each.step_id);
            }}
          >
            <OutcomeBar step={row.original.steps.find((found) => found.step_id === each.step_id)} />
          </button>
        ),
      }),
    ),
    {
      id: "unexplained",
      header: "Nem magyarázott",
      cell: ({ row }) => count(Number(row.original.counts.unexplained ?? 0)),
      meta: { align: "right", mono: true },
    },
    {
      id: "explained",
      header: "Magyarázott",
      cell: ({ row }) => count(Number(row.original.counts.explained ?? 0)),
      meta: { align: "right", mono: true, muted: true },
    },
    {
      id: "missing_info",
      header: "Hiányzó adat",
      cell: ({ row }) => count(Number(row.original.counts.missing_info ?? 0)),
      meta: { align: "right", mono: true, muted: true },
    },
  ];

  const itemColumns: ColumnDef<FlowComparisonItem>[] = [
    { id: "day", header: "Nap", cell: ({ row }) => row.original.day, meta: { mono: true } },
    { id: "step", header: "Lépés", cell: ({ row }) => (row.original.step_id ? `#${row.original.step_id}` : "—"), meta: { mono: true } },
    {
      id: "profile",
      header: "Profil",
      cell: ({ row }) => <span className="font-mono">{row.original.profile_source_id ?? "—"}</span>,
    },
    {
      id: "outcome",
      header: "Kimenet",
      cell: ({ row }) => {
        const look = outcomeLook(row.original.outcome);
        return (
          <StatusPill tone={row.original.explained || row.original.missing_info ? "muted" : look.tone} title={row.original.outcome}>
            {look.label}
            {row.original.explained ? " · magyarázott" : ""}
            {row.original.missing_info ? " · hiányzó adat" : ""}
          </StatusPill>
        );
      },
    },
    { id: "reason", header: "Ok", cell: ({ row }) => row.original.reason_label || "—", meta: { wrap: true } },
    { id: "ours", header: "Mi", cell: ({ row }) => minute(row.original.ours_at), meta: { mono: true } },
    { id: "theirs", header: "Klaviyo", cell: ({ row }) => minute(row.original.klaviyo_at), meta: { mono: true } },
    { id: "delta", header: "Δ", cell: ({ row }) => deltaText(row.original.delta_seconds), meta: { align: "right", mono: true } },
  ];

  return (
    <div>
      <PageHeader
        back={{ to: `/flowk/${flowId}`, label: data?.name ?? "Flow" }}
        title={data ? `Összevetés: ${data.name}` : "Összevetés"}
        description="A mi árnyék-döntéseink és a Klaviyo valódi levelei lépésenként és naponta. Egy cellára kattintva az alábbi lista arra a napra és lépésre szűkül; egy eltérésre kattintva a profil két idővonala nyílik meg."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/flowk/osszevetes">Minden flow</Link>
          </Button>
        }
      />
      {detail.error !== null && <ErrorState message={detail.error} onRetry={reloadDetail} className="mb-4" />}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Napok és lépések</CardTitle>
          <CardDescription>
            Egy nap akkor végleges, ha minden jel és a motor 48 órával túl van rajta; addig a szám „eddig” jelű.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Toolbar>
            <Field label="Időszak" htmlFor="compare-range">
              <NativeSelect id="compare-range" value={range} onChange={(event) => setRange(event.target.value)}>
                {RANGES.map((each) => (
                  <option key={each.value} value={each.value}>
                    {each.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </Toolbar>
          {data === null && detail.error === null ? (
            <LoadingState lines={3} />
          ) : (
            <DataTable
              columns={dayColumns}
              data={days}
              getRowId={(row) => row.day}
              variant="plain"
              label="Napok"
              empty={
                <EmptyState
                  bare
                  title="Még nincs összevetett nap"
                  description="A motor köre óránként veti össze a napokat; egy nap a 48 órás ráhagyás után lesz végleges."
                />
              }
            />
          )}
          {data && (data.branches ?? []).length > 0 && <BranchShares splits={data.branches ?? []} />}
          {data && data.explained_policy.length > 0 && (
            <Callout tone="info">
              <p className="m-0 font-medium">Magyarázott (tulajdonosi szabály):</p>
              <ul className="m-0 mt-1 pl-5">
                {data.explained_policy.map((each) => (
                  <li key={each.reason}>
                    <span className="font-mono">{each.reason}</span> — {each.why}
                  </li>
                ))}
              </ul>
            </Callout>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Eltérések</CardTitle>
          <CardDescription>
            Alapból minden eltérés (egyezés és ablakon kívüli nélkül). Egy sorra kattintva a profil két idővonala.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Toolbar>
            <Field label="Kimenet" htmlFor="compare-outcome">
              <NativeSelect id="compare-outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
                <option value="">Minden eltérés</option>
                {OUTCOMES.map((each) => (
                  <option key={each} value={each}>
                    {outcomeLook(each).label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Ok" htmlFor="compare-reason">
              <NativeSelect id="compare-reason" value={reason} onChange={(event) => setReason(event.target.value)}>
                <option value="">Mind</option>
                {(data?.reasons ?? []).map((each) => (
                  <option
                    key={`${each.outcome}-${each.reason}-${each.explained}-${each.missing_info ?? ""}`}
                    value={each.reason}
                  >
                    {each.label} ({count(each.count)}){each.explained ? " · magyarázott" : ""}
                    {each.missing_info ? " · hiányzó adat" : ""}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Nap" htmlFor="compare-day">
              <NativeSelect id="compare-day" value={day} onChange={(event) => setDay(event.target.value)}>
                <option value="">Mind</option>
                {days.map((each) => (
                  <option key={each.day} value={each.day}>
                    {each.day}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Lépés" htmlFor="compare-step">
              <NativeSelect id="compare-step" value={step} onChange={(event) => setStep(event.target.value)}>
                <option value="">Mind</option>
                {steps.map((each) => (
                  <option key={each.step_id} value={each.step_id}>
                    #{each.step_id}
                    {each.name ? ` · ${each.name}` : ""}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </Toolbar>
          {list.error !== null && <ErrorState message={list.error} onRetry={reloadList} />}
          <DataTable
            columns={itemColumns}
            data={items}
            getRowId={(row) => String(row.id)}
            onRowClick={(row) => setOpen(row)}
            isRowSelected={(row) => open?.id === row.id}
            loading={list.data === null && list.error === null}
            variant="plain"
            label="Eltérések"
            empty={<EmptyState bare title="Nincs eltérés" description="A szűrőnek megfelelő tétel nincs." />}
            footer={
              <TablePager summary={`${count(items.length)} tétel`}>
                {cursor !== null && (
                  <Button variant="outline" size="sm" onClick={() => void more()}>
                    Továbbiak
                  </Button>
                )}
              </TablePager>
            }
          />
        </CardContent>
      </Card>
      <Timeline flowId={flowId} item={open} onClose={() => setOpen(null)} />
    </div>
  );
}


/** "48%": a share of the letters, or a dash when there are none. */
function shareText(share: number | null): string {
  return share === null ? "—" : `${Math.round(share * 100)}%`;
}

/** Each multi-branch (or random) split's ways: our letters and Klaviyo's
 * right after each way, and the two shares side by side. A random split
 * cannot agree per profile; its shares must. */
function BranchShares({ splits }: { splits: FlowComparisonSplit[] }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 font-medium">Elágazások: az ágak aránya a két oldalon</p>
      {splits.map((split) => (
        <div key={split.step_id} className="flex flex-col gap-1 rounded-inner border border-line p-2 text-detail">
          <p className="m-0">
            {split.name ?? "Elágazás"} <span className="data-mono text-label text-muted-foreground">#{split.step_id}</span>
            {split.random ? <span className="text-muted-foreground"> · véletlen minta: profilonként nem egyezhet, az arány igen</span> : null}
          </p>
          <table className="w-full text-detail">
            <thead>
              <tr className="text-left text-label text-muted-foreground">
                <th className="font-normal">Ág</th>
                <th className="text-right font-normal">mi</th>
                <th className="text-right font-normal">Klaviyo</th>
                <th className="text-right font-normal">arány (mi / Klaviyo)</th>
              </tr>
            </thead>
            <tbody>
              {split.ways.map((way) => (
                <tr key={way.answer}>
                  <td>{way.else ? "Egyéb" : (way.name ?? way.answer)}</td>
                  <td className="data-mono text-right">{count(way.ours)}</td>
                  <td className="data-mono text-right">{count(way.klaviyo)}</td>
                  <td className="data-mono text-right">
                    {shareText(way.ours_share)} / {shareText(way.klaviyo_share)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
